'use client';

import { useState, useEffect } from 'react';
import { useGetListsQuery } from 'src/api/redux/lists/listsApi';
import { stringToColor } from 'src/utils/color.helpers';
import { Typography } from 'src/components';
import { Tooltip, Dropdown } from 'src/components/PulseUI';

interface AvatarFilterGroupProps {
  max?: number;
  onFilter: (values: any[] | null) => void;
}

interface AvatarProps {
  initials: string;
  bgColor: string;
  active?: boolean;
  onClick?: () => void;
  className?: string;
}

const Avatar = ({ initials, bgColor, active, onClick, className = '' }: AvatarProps) => (
  <button
    onClick={onClick}
    type="button"
    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm cursor-pointer transition-all
      ${active ? 'ring-2 ring-sky-600 ring-offset-2' : 'border-2 border-white'}
      ${className}`}
    style={{ backgroundColor: bgColor }}
    aria-pressed={active}
    data-testid="avatar-button"
  >
    <Typography>{initials}</Typography>
  </button>
);

interface AvatarGroupProps {
  children: React.ReactNode;
  max: number;
  onSurplusClick?: () => void;
  surplus: number;
}

const AvatarGroup = ({ children, max, onSurplusClick, surplus }: AvatarGroupProps) => {
  const childArray = Array.isArray(children) ? children : [children];
  const visible = childArray.slice(0, max - 1);

  return (
    <div className="flex items-center -space-x-2" data-testid="avatar-group">
      {visible}
      {surplus > 0 && (
        <button
          type="button"
          onClick={onSurplusClick}
          className="w-10 h-10 rounded-full flex items-center justify-center bg-sky-100 text-sky-800 font-medium text-sm cursor-pointer border-2 border-white hover:bg-sky-200 transition-colors"
          aria-label={`Show ${surplus} more users`}
          data-testid="avatar-surplus-button"
        >
          +{surplus}
        </button>
      )}
    </div>
  );
};

const AvatarFilterGroup = ({ max = 15, onFilter }: AvatarFilterGroupProps) => {
  const { data: providerLists } = useGetListsQuery({});
  const [active, setActive] = useState<any[] | null>(null);

  useEffect(() => {
    if (!active) return;
    onFilter(active);
  }, [active]);

  const handleFilter = (id: number) => {
    setActive((prev) => {
      if (!prev) return [id];
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  // Get unique owners from providerLists
  const getOwners = () => {
    if (!providerLists) return [];
    const owners = providerLists.results.map((list) => list.owner);
    // Remove duplicates
    const uniqueOwners = Array.from(new Set(owners.map((a) => a.id))).map(
      (id) => {
        return owners.find((a) => a.id === id);
      }
    );

    return uniqueOwners.sort((a, b) => {
      return a.first_name.localeCompare(b.first_name);
    });
  };

  const getInitials = (name: string) => {
    const nameParts = name.split(' ');
    return `${nameParts[0][0]}${nameParts[1][0]}`;
  };

  const owners = getOwners();
  const displayedOwners = owners.slice(0, max - 1);
  const remainingOwners = owners.slice(max - 1);
  const surplus = remainingOwners.length;

  return (
    <div
      className="w-full flex justify-start"
      data-testid="avatar-filter-group"
    >
      {surplus > 0 ? (
        <Dropdown
          options={remainingOwners.map((avatar) => ({
            label: `${avatar.first_name} ${avatar.last_name}`,
            onClick: () => handleFilter(avatar.id),
          }))}
          slots={{
            trigger: {
              component: (
                <AvatarGroup
                  max={max}
                  surplus={surplus}
                >
                  {displayedOwners.map((avatar, index) => {
                    const fullName = `${avatar.first_name} ${avatar.last_name}`;
                    return (
                      <Tooltip key={index} content={fullName} position="top">
                        <Avatar
                          initials={getInitials(fullName)}
                          bgColor={stringToColor(fullName)}
                          active={active?.includes(avatar.id)}
                          onClick={() => handleFilter(avatar.id)}
                        />
                      </Tooltip>
                    );
                  })}
                </AvatarGroup>
              ),
            },
            option: {
              component: (option) => {
                const avatar = remainingOwners.find(
                  (a) => `${a.first_name} ${a.last_name}` === option.label
                );
                if (!avatar) return <Typography>{option.label}</Typography>;
                const fullName = `${avatar.first_name} ${avatar.last_name}`;
                return (
                  <div className="flex items-center gap-2">
                    <Avatar
                      initials={getInitials(fullName)}
                      bgColor={stringToColor(fullName)}
                      active={active?.includes(avatar.id)}
                      className="w-8 h-8 text-xs"
                    />
                    <Typography>{fullName}</Typography>
                  </div>
                );
              },
            },
          }}
          position="bottom-left"
          ariaLabel="Additional users filter menu"
        />
      ) : (
        <div className="flex items-center -space-x-2">
          {owners.map((avatar, index) => {
            const fullName = `${avatar.first_name} ${avatar.last_name}`;
            return (
              <Tooltip key={index} content={fullName} position="top">
                <Avatar
                  initials={getInitials(fullName)}
                  bgColor={stringToColor(fullName)}
                  active={active?.includes(avatar.id)}
                  onClick={() => handleFilter(avatar.id)}
                />
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvatarFilterGroup;
